/**
 * Tasks Service
 *
 * Pure business logic for task operations and transformations.
 * No React or Firebase dependencies.
 */

import { Task, TaskPriority, TaskStatus } from '@/types';

// ============================================
// Priority Helpers
// ============================================

/**
 * Get a human-readable label for a task priority.
 *
 * @param priority - The task priority
 * @returns Human-readable priority label
 */
export function getPriorityLabel(priority: TaskPriority): string {
  switch (priority) {
    case 'high':
      return 'High priority';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return '';
  }
}

// ============================================
// Task Filtering
// ============================================

/**
 * Filter tasks by their status.
 *
 * @param tasks - Array of tasks to filter
 * @param status - Status to filter by, or 'all' to return all tasks
 * @returns Filtered array of tasks
 */
export function filterTasksByStatus(tasks: Task[], status: TaskStatus | 'all'): Task[] {
  if (status === 'all') {
    return tasks;
  }
  return tasks.filter((task) => task.status === status);
}

/**
 * Get tasks that are pending (not scheduled or completed).
 *
 * @param tasks - Array of tasks to filter
 * @returns Array of pending tasks
 */
export function getPendingTasks(tasks: Task[]): Task[] {
  return tasks.filter((task) => task.status === 'pending');
}

/**
 * Get tasks that are scheduled to a block.
 *
 * @param tasks - Array of tasks to filter
 * @returns Array of scheduled tasks
 */
export function getScheduledTasks(tasks: Task[]): Task[] {
  return tasks.filter((task) => task.status === 'scheduled');
}

/**
 * Get completed tasks.
 *
 * @param tasks - Array of tasks to filter
 * @returns Array of completed tasks
 */
export function getCompletedTasks(tasks: Task[]): Task[] {
  return tasks.filter((task) => task.status === 'completed');
}

// ============================================
// Task Grouping
// ============================================

/**
 * Group tasks by their category ID.
 *
 * @param tasks - Array of tasks to group
 * @returns Map of categoryId to array of tasks
 */
export function groupTasksByCategory(tasks: Task[]): Map<string, Task[]> {
  const grouped = new Map<string, Task[]>();

  for (const task of tasks) {
    const existing = grouped.get(task.categoryId) || [];
    grouped.set(task.categoryId, [...existing, task]);
  }

  return grouped;
}

/**
 * Group tasks by their priority.
 *
 * @param tasks - Array of tasks to group
 * @returns Map of priority to array of tasks
 */
export function groupTasksByPriority(tasks: Task[]): Map<TaskPriority, Task[]> {
  const grouped = new Map<TaskPriority, Task[]>();

  for (const task of tasks) {
    const existing = grouped.get(task.priority) || [];
    grouped.set(task.priority, [...existing, task]);
  }

  return grouped;
}

// ============================================
// Task Meta Information
// ============================================

/**
 * Get formatted meta information string for a task.
 *
 * @param task - The task to get meta info for
 * @param formatDuration - Function to format duration in minutes
 * @returns Formatted meta info string with bullet separators
 */
export function getTaskMetaInfo(task: Task, formatDuration: (minutes: number) => string): string {
  const parts: string[] = [];

  if (task.estimatedDuration) {
    parts.push(`Est: ${formatDuration(task.estimatedDuration)}`);
  }

  parts.push(getPriorityLabel(task.priority));

  if (task.dueDate) {
    parts.push(`Due: ${task.dueDate}`);
  }

  return parts.join(' \u2022 '); // Bullet separator
}

// ============================================
// Task Sorting
// ============================================

/**
 * Sort tasks by their order field.
 *
 * @param tasks - Array of tasks to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortTasksByOrder(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => a.order - b.order);
}

/**
 * Sort tasks by priority (high > medium > low).
 *
 * @param tasks - Array of tasks to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortTasksByPriority(tasks: Task[]): Task[] {
  const priorityOrder: Record<TaskPriority, number> = {
    high: 0,
    medium: 1,
    low: 2,
  };

  return [...tasks].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
}
